import { useEffect, useState } from "react";
import { useFormikContext } from "formik";
import PropTypes from "prop-types";
import { constants } from "@qgiv/core-js";
import { useDebounce } from "@qgiv/core-react";
import { getCustomFields } from "@qgiv/p2p-registration";
import { useDispatch } from "react-redux";

const UpdateFields = ({
    currentRegistration,
    formId,
    fetchFields,
    hasParentRegistration,
}) => {
    const dispatch = useDispatch();
    const { values, errors, touched, setFieldTouched, dirty, isSubmitting } =
        useFormikContext();

    const { Category, Registration_Role, Is_Nfp, Team } = values;

    const { ENUMS } = constants;

    const {
        EntityType: { REGISTRATION, TEAM },
    } = ENUMS;

    /// /////////////////////////////////////////////////////////////////////////
    // Note: Team members will send join_team.
    //       Team captains will send create_team.
    /// /////////////////////////////////////////////////////////////////////////
    const getRole = () => {
        switch (Registration_Role[0]) {
            case `${TEAM}`: {
                return Team === "new-team" && currentRegistration === 0
                    ? "create_team"
                    : "join_team";
            }
            case `${REGISTRATION}`: {
                return "individual";
            }
            default:
                return "";
        }
    };

    const getParams = () => ({
        role: getRole(),
        type:
            // Note: Parent Registrations come from Fundraising Dashboard and even though
            // the registrations that follow have a currentRegistration of 0, they
            // are actually additional registrants under the parent as primary.
            currentRegistration > 0 || hasParentRegistration
                ? "subregistrant"
                : "primary",
        category: Category[0] ? Category[0] : "",
        is_nfp: Is_Nfp || "",
    });

    // All available Custom_Fields are set as InitialValues and InitialTouched
    // Once a registration is saved to redux (form submission), the Custom_Fields that did not display for that registration (per setting limitations)
    // are removed from the value/touched Objects.  We are using Custom Form Level validation for these fields.
    // Therefore if we switch Categories causing new Custom_Field to display
    // we must manually set that field to touched on submission of those changes so Formik can recognize the
    // error should be displayed. This should catch any field that has an error however has not been touched on the previous form submission.
    useEffect(() => {
        if (dirty && isSubmitting) {
            // eslint-disable-next-line array-callback-return
            Object.keys(errors).map((error) => {
                if (!Object.keys(touched).includes(error))
                    setFieldTouched(error);
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [errors, isSubmitting]);

    const [params, setParams] = useState(getParams());

    useEffect(() => {
        setParams(getParams());
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [Category, Registration_Role, Is_Nfp, Team, currentRegistration]);

    // Passing params through useDebounce to make sure we aren't
    // making a network call before the last call has finished
    // This was originally 300ms, but was bumped up to 2200ms
    // There is an edge case with there is only one category
    // available for a classification, and the only role available is
    // individual that would cause 3 custom field api calls to be made
    // relatively close together. The last call would be outside of the
    // timeout buffer for debounce depending on internet connected speed.
    // This value was tested with "Slow 3G" throttling in chrome.
    const debouncedParams = useDebounce(params, 2200);

    useEffect(() => {
        if (debouncedParams) {
            getCustomFields(formId, debouncedParams).then((response) => {
                const {
                    data: {
                        response: { custom_fields = [] },
                    },
                } = response;
                dispatch(fetchFields(custom_fields));
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [debouncedParams]);

    return null;
};

UpdateFields.propTypes = {
    currentRegistration: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.number,
    ]).isRequired,
    fetchFields: PropTypes.func.isRequired,
    formId: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
        .isRequired,
    hasParentRegistration: PropTypes.bool.isRequired,
};

export default UpdateFields;
